<script setup lang="ts">
import { parseLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  canCreateEntries,
  DefaultPaymentAccountPicker,
  SplitExpenseForm,
  useDefaultPaymentAccount,
  useEntryTargets,
  useSplitExpense,
} from '@/features/entries'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const router = useRouter()

const canCreate = computed(() => canCreateEntries(session.can))
const ledgerId = () => session.activeLedger.value?.id
const activeLocale = () => locale.value as 'en' | 'hu'
const today = () => todayUtc(systemClock)

const targets = useEntryTargets({
  ledgerId,
  locale: activeLocale,
  enabled: () => canCreate.value,
  canCreateContact: () => session.can('RECORDS_MANAGE'),
})
const targetsById = computed(() => targets.targetsById.value)
const counterTargets = computed(() => targets.counterTargets(['EXPENSE']))
// Set-all's category axis never touches a contact line (myWealth §4.4) - offer only
// category-class targets here, not the merged namespace the per-line picker uses.
const categoryTargets = computed(() => counterTargets.value.filter((target) => target.source === 'account'))

// The default payment account is class ASSET only (myWealth §4.2), never a contact and
// never a LIABILITY account - a narrower scope than the money-side picker this replaced.
const paymentAccountTargets = computed(() =>
  targets.counterTargets(['ASSET']).filter((target) => target.source === 'account'),
)
const rawDefaultAccountId = computed(() => session.activeLedger.value?.defaultPaymentAccountId)
// An archived or deleted default is treated as no default at all (myWealth §4.8) - the
// gate below re-triggers rather than silently falling back to some other account.
const defaultAccountIsValid = computed(
  () =>
    rawDefaultAccountId.value !== undefined &&
    paymentAccountTargets.value.some((target) => target.id === rawDefaultAccountId.value),
)
const needsDefaultPaymentAccount = computed(() => !targets.isLoading.value && !defaultAccountIsValid.value)
const paymentAccountName = computed(() => targetsById.value.get(rawDefaultAccountId.value ?? '')?.leaf)

const defaultPaymentAccount = useDefaultPaymentAccount({ ledgerId })

function onSetDefaultPaymentAccount(accountId: string): void {
  void defaultPaymentAccount.set(accountId)
}

const split = useSplitExpense({
  ledgerId,
  targetsById: () => targetsById.value,
  today,
  defaultAccountId: () => (defaultAccountIsValid.value ? rawDefaultAccountId.value : undefined),
})
const setAllCategoryId = ref<string | undefined>(undefined)

async function onSubmit(): Promise<void> {
  await split.submit()
  if (split.submitError.value === null) void router.push({ name: 'transactions' })
}

function onDone(): void {
  void router.push({ name: 'transactions' })
}

function onDateChange(value: string): void {
  split.row.value.date = parseLocalDate(value)
}
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-[var(--space-section)] p-[var(--space-page)]">
    <div class="flex items-center justify-between gap-[var(--space-group)]">
      <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
        {{ t('splitExpense.title') }}
      </h1>
      <button
        type="button"
        class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] underline"
        @click="onDone"
      >
        {{ t('entries.backToTransactions') }}
      </button>
    </div>
    <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
      {{ t('splitExpense.subtitle') }}
    </p>

    <p v-if="session.isLoading.value || (canCreate && targets.isLoading.value)" role="status">
      {{ t('app.loading') }}
    </p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('entries.noLedger') }}
    </p>
    <p v-else-if="!canCreate" class="text-[color:var(--color-text-muted)]">
      {{ t('entries.noAccess') }}
    </p>

    <div v-else-if="needsDefaultPaymentAccount" class="flex flex-col gap-[var(--space-block)]">
      <p class="text-[color:var(--color-text-muted)]">{{ t('paymentAccount.gateExplanation') }}</p>
      <DefaultPaymentAccountPicker
        :account-id="rawDefaultAccountId"
        :targets="paymentAccountTargets"
        :frecency="targets.frecency.value"
        :is-saving="defaultPaymentAccount.isSetting.value"
        :error-detail="defaultPaymentAccount.errorDetail.value"
        @save="onSetDefaultPaymentAccount"
      />
    </div>

    <template v-else>
      <SplitExpenseForm
        v-model:set-all-category-id="setAllCategoryId"
        :row="split.row.value"
        :payee="split.payee.value"
        :total="split.total.value"
        :payment-account-name="paymentAccountName"
        :counter-targets="counterTargets"
        :category-targets="categoryTargets"
        :targets-by-id="targetsById"
        :frecency="targets.frecency.value"
        :remaining="split.remaining.value"
        :can-submit="split.canSubmit.value"
        :is-submitting="split.isSubmitting.value"
        :has-submit-error="split.submitError.value !== null"
        :submit-error-detail="split.submitErrorDetail.value"
        :just-posted="split.justPosted.value"
        @update:payee="(value) => (split.payee.value = value)"
        @update:total="(value) => (split.total.value = value)"
        @update:date="onDateChange"
        @set-target="(splitId, targetId) => split.setTarget(splitId, targetId)"
        @add-line="split.addLine()"
        @remove-line="(splitId) => split.removeLine(splitId)"
        @split-evenly="split.splitEvenly()"
        @apply-category-to-all="(categoryTargetId) => split.applyCategoryToAll(categoryTargetId)"
        @submit="onSubmit()"
      />
    </template>
  </main>
</template>
